import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, Shield, Settings, CheckCircle, XCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { toast } from '../../../components/ui/Toast';
import securityAlertService from '../../../services/securityAlertService';
import Icon from '../../../components/AppIcon';


const SecurityAlertsDashboard = () => {
  const [thresholds, setThresholds] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('alerts'); // 'alerts', 'thresholds'
  
  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterAcknowledged, setFilterAcknowledged] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    loadData();
  }, [filterType, filterSeverity, filterAcknowledged, currentPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [thresholdsData, alertsData, statsData] = await Promise.all([
        securityAlertService?.getAlertThresholds(),
        securityAlertService?.getAlerts({
          alertType: filterType || null,
          severity: filterSeverity || null,
          acknowledged: filterAcknowledged === '' ? null : filterAcknowledged === 'true',
          limit: pageSize,
          offset: currentPage * pageSize
        }),
        securityAlertService?.getAlertStatistics(30)
      ]);

      setThresholds(thresholdsData);
      setAlerts(alertsData?.alerts);
      setTotalAlerts(alertsData?.total);
      setStatistics(statsData);
    } catch (error) {
      console.error('Error loading security alerts data:', error);
      toast?.error('Failed to load security alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveThresholds = async () => {
    setSaving(true);
    try {
      await securityAlertService?.updateAlertThresholds(thresholds);
      toast?.success('Alert thresholds updated successfully');
    } catch (error) {
      console.error('Error saving thresholds:', error);
      toast?.error('Failed to save alert thresholds');
    } finally {
      setSaving(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await securityAlertService?.acknowledgeAlert(alertId);
      toast?.success('Alert acknowledged');
      loadData();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast?.error('Failed to acknowledge alert');
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'text-blue-600 bg-blue-50',
      medium: 'text-yellow-600 bg-yellow-50',
      high: 'text-orange-600 bg-orange-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors?.[severity] || colors?.low;
  };

  const getAlertTypeIcon = (type) => {
    const icons = {
      suspicious_login: AlertTriangle,
      permission_change: Shield,
      policy_violation: XCircle
    };
    return icons?.[type] || Bell;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-6 h-6 text-blue-600" />
              Security Alerts
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Monitor and configure security alerts for suspicious activities
            </p>
          </div>
          {statistics && (
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{statistics?.total}</div>
                <div className="text-xs text-gray-600">Total Alerts</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{statistics?.unacknowledged}</div>
                <div className="text-xs text-gray-600">Unacknowledged</div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex px-6">
          <button
            onClick={() => setActiveTab('alerts')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'alerts' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Alert History
          </button>
          <button
            onClick={() => setActiveTab('thresholds')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'thresholds' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-1" />
            Alert Configuration
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        {activeTab === 'alerts' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alert Type
                </label>
                <Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e?.target?.value);
                    setCurrentPage(0);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="suspicious_login">Suspicious Login</option>
                  <option value="permission_change">Permission Change</option>
                  <option value="policy_violation">Policy Violation</option>
                </Select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity
                </label>
                <Select
                  value={filterSeverity}
                  onChange={(e) => {
                    setFilterSeverity(e?.target?.value);
                    setCurrentPage(0);
                  }}
                >
                  <option value="">All Severities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Select
                  value={filterAcknowledged}
                  onChange={(e) => {
                    setFilterAcknowledged(e?.target?.value);
                    setCurrentPage(0);
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="false">Unacknowledged</option>
                  <option value="true">Acknowledged</option>
                </Select>
              </div>
            </div>

            {/* Alerts List */}
            <div className="space-y-3">
              {alerts?.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No security alerts found</p>
                </div>
              ) : (
                alerts?.map((alert) => {
                  const Icon = getAlertTypeIcon(alert?.alert_type);
                  return (
                    <div
                      key={alert?.id}
                      className={`border rounded-lg p-4 ${
                        alert?.acknowledged ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <Icon className={`w-5 h-5 mt-1 ${getSeverityColor(alert?.severity)?.split(' ')?.[0]}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{alert?.title}</h3>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded ${getSeverityColor(alert?.severity)}`}>
                                {alert?.severity?.toUpperCase()}
                              </span>
                              {alert?.acknowledged && (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{alert?.description}</p>
                            <div className="flex gap-4 text-xs text-gray-500">
                              <span>Type: {alert?.alert_type?.replace('_', ' ')}</span>
                              {alert?.ip_address && <span>IP: {alert?.ip_address}</span>}
                              {alert?.device_info && <span>Device: {alert?.device_info}</span>}
                              <span>{new Date(alert.created_at)?.toLocaleString()}</span>
                            </div>
                            {alert?.threshold_exceeded && Object.keys(alert?.threshold_exceeded)?.length > 0 && (
                              <div className="mt-2 text-xs text-gray-600 bg-gray-100 rounded p-2">
                                <strong>Threshold Details:</strong> {JSON.stringify(alert?.threshold_exceeded)}
                              </div>
                            )}
                          </div>
                        </div>
                        {!alert?.acknowledged && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcknowledgeAlert(alert?.id)}
                          >
                            Acknowledge
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalAlerts > pageSize && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalAlerts)} of {totalAlerts} alerts
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={(currentPage + 1) * pageSize >= totalAlerts}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'thresholds' && thresholds && (
          <div className="space-y-6">
            {/* Suspicious Login Thresholds */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                Suspicious Login Detection
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Failed Login Attempts Threshold
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={thresholds?.failed_login_attempts}
                    onChange={(e) => setThresholds({ ...thresholds, failed_login_attempts: parseInt(e?.target?.value) })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Alert after this many failed attempts</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time Window (minutes)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={thresholds?.failed_login_window_minutes}
                    onChange={(e) => setThresholds({ ...thresholds, failed_login_window_minutes: parseInt(e?.target?.value) })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Within this time period</p>
                </div>
                <div className="col-span-2 space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={thresholds?.new_device_alert_enabled}
                      onChange={(e) => setThresholds({ ...thresholds, new_device_alert_enabled: e?.target?.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Alert on new device login</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={thresholds?.unusual_location_alert_enabled}
                      onChange={(e) => setThresholds({ ...thresholds, unusual_location_alert_enabled: e?.target?.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Alert on unusual location</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Permission Change Thresholds */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                Permission Change Detection
              </h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.role_escalation_alert_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, role_escalation_alert_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Alert on role escalation</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.permission_grant_alert_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, permission_grant_alert_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Alert on permission grants</span>
                </label>
              </div>
            </div>

            {/* Policy Violation Thresholds */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                Policy Violation Detection
              </h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.password_policy_violation_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, password_policy_violation_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Alert on password policy violations</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.compliance_violation_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, compliance_violation_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Alert on compliance violations</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.data_access_violation_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, data_access_violation_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Alert on unauthorized data access</span>
                </label>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="border rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.email_alerts_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, email_alerts_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Send email alerts</span>
                </label>
                {thresholds?.email_alerts_enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Alert Email Address
                    </label>
                    <Input
                      type="email"
                      value={thresholds?.alert_email || ''}
                      onChange={(e) => setThresholds({ ...thresholds, alert_email: e?.target?.value })}
                      placeholder="your-email@example.com"
                    />
                  </div>
                )}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={thresholds?.sms_alerts_enabled}
                    onChange={(e) => setThresholds({ ...thresholds, sms_alerts_enabled: e?.target?.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Send SMS alerts</span>
                </label>
                {thresholds?.sms_alerts_enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SMS Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={thresholds?.sms_phone_number || ''}
                      onChange={(e) => setThresholds({ ...thresholds, sms_phone_number: e?.target?.value })}
                      placeholder="+1234567890"
                    />
                    <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +1 for US)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveThresholds}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Alert Configuration'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityAlertsDashboard;